import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { IconButton, Box, Typography, List, ListItem, ListItemText, Chip, Divider, Paper, useTheme } from '@mui/material';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import TopCompetitorsPanel from './components/TopCompetitorsPanel';
import { borderRadius, shadows } from './theme/tokens';

// OneQrew company marker with star icon and pulse effect
const createCompanyMarker = (name) => {
  return L.divIcon({
    className: 'custom-marker company-marker',
    html: `
      <div class="marker-container">
        <div style="background-color: #F0F55F; color: #190023; border-radius: 50%; width: 46px; height: 46px; display: flex; align-items: center; justify-content: center; font-size: 18px; border: 3px solid white; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15); animation: pulse 2s infinite;">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#190023"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
        </div>
        <div style="position: absolute; left: 50%; transform: translateX(-50%); bottom: -24px; white-space: nowrap; background-color: rgba(240, 245, 95, 0.95); color: #190023; padding: 4px 8px; border-radius: 8px; font-weight: bold; font-size: 10px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);">Ihr Unternehmen</div>
      </div>
    `,
    iconSize: [46, 46],
    iconAnchor: [23, 46],
    popupAnchor: [0, -46],
  });
};

// OneQrew rank marker with traffic light colors (Ampelsystem)
const createRankMarker = (rank, color) => {
  // Traffic light system: Green (1-3), Orange (4-10), Red (11+)
  let gradientColor;
  let textColor;

  if (rank <= 3) {
    // Green for top rankings
    gradientColor = 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)';
    textColor = 'white';
  } else if (rank <= 10) {
    // Orange for good rankings
    gradientColor = 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)';
    textColor = 'white';
  } else {
    // Red for lower rankings
    gradientColor = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
    textColor = 'white';
  }

  return L.divIcon({
    className: 'custom-marker rank-marker',
    html: `
      <div style="background: ${gradientColor}; color: ${textColor}; border-radius: 50%; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; font-size: 15px; font-weight: 500; border: 2px solid white; box-shadow: 0 3px 5px rgba(0, 0, 0, 0.2); text-align: center; transition: all 0.3s ease;">
        ${rank > 20 ? '20+' : rank}
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
};

// Component für automatische Zoom-Anpassung
const AutoFitBounds = ({ companyLocation, locationRanks }) => {
  const map = useMap();
  
  useEffect(() => {
    if (!locationRanks || locationRanks.length === 0) return;
    
    // Alle Koordinaten sammeln
    const allPoints = [...locationRanks];
    if (companyLocation) {
      allPoints.push(companyLocation);
    }
    
    if (allPoints.length > 0) {
      // Bounds für alle Punkte erstellen
      const group = new L.featureGroup(
        allPoints.map(point => L.marker([point.lat, point.lng]))
      );
      
      // Map auf alle Punkte zoomen mit Padding
      map.fitBounds(group.getBounds(), {
        padding: [20, 20],
        maxZoom: 12
      });
    }
  }, [map, companyLocation, locationRanks]);
  
  return null;
};

const LocationMap = ({ companyLocation, companyName, locationRanks, keywordVolume, searchKeyword, topCompetitors, isLocationMode }) => {
  const theme = useTheme();
  const center = companyLocation ? [companyLocation.lat, companyLocation.lng] : [0, 0];
  const [openPopup, setOpenPopup] = useState(null);

  // Stelle companyLocation global zur Verfügung für Keyword-Volume API
  useEffect(() => {
    if (companyLocation) {
      window.currentCompanyLocation = companyLocation;
      console.log('🗺️ Set global company location:', companyLocation);
    }
  }, [companyLocation]);

  const handleClosePopup = () => {
    setOpenPopup(null);
  };

  // Funktion, um die Farbe basierend auf dem Rang zu erhalten (Ampelsystem)
  const getColorForRank = (rank) => {
    if (rank <= 3) return theme.palette.success.main;   // Grün für Top 3
    if (rank <= 10) return theme.palette.warning.main;  // Orange für 4-10
    return theme.palette.error.main;                    // Rot für 11+
  };

  // Funktion für Kreis-Opacity basierend auf dem Rang
  const getOpacityForRank = (rank) => {
    if (rank <= 3) return 0.2;       // Top 3 - deutlicher sichtbar
    if (rank <= 10) return 0.15;     // Bis 10 - mittlere Sichtbarkeit
    return 0.1;                      // 11+ - subtil im Hintergrund
  };

  // Funktion für Radius (in Metern) basierend auf dem Rang
  const getRadiusForRank = (rank) => {
    if (rank <= 3) return 800;       // Top 3 - größerer Einflussbereich
    if (rank <= 10) return 600;      // Bis 10 - mittlerer Einflussbereich
    return 400;                      // 11+ - kleiner Einflussbereich
  };

  // Status-Label basierend auf dem Rang (Ampelsystem)
  const getRankStatus = (rank) => {
    if (rank <= 3) return { text: 'Top Position', color: theme.palette.success.main };
    if (rank <= 10) return { text: 'Gute Position', color: theme.palette.warning.main };
    return { text: 'Optimierungsbedarf', color: theme.palette.error.main };
  };

  return (
    <MapContainer
      center={center}
      zoom={11}
      style={{ height: '500px', width: '100%', borderRadius: borderRadius.xxs, boxShadow: shadows.md }}
      onClick={() => setOpenPopup(null)}
    >
      <AutoFitBounds companyLocation={companyLocation} locationRanks={locationRanks} />
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='© <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
      />
      
      {/* Top Competitors Panel - LINKS */}
      {console.log('🏆 TOP COMPETITORS DEBUG:', { topCompetitors, searchKeyword, hasCompetitors: !!topCompetitors, hasKeyword: !!searchKeyword })}
      {topCompetitors && topCompetitors.length > 0 && searchKeyword && (
        <TopCompetitorsPanel 
          competitors={topCompetitors} 
          keyword={searchKeyword} 
        />
      )}
      
      {/* Location Mode Badge - OneQrew Design */}
      {isLocationMode && (
        <div
          style={{
            position: 'absolute',
            top: '20px',
            left: '10px',
            zIndex: 1000,
            backgroundColor: theme.palette.oneqrew.primaryContainer,
            backdropFilter: 'blur(8px)',
            borderRadius: '8px',
            padding: '8px 16px',
            boxShadow: '0 4px 12px rgba(25, 0, 35, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill={theme.palette.oneqrew.primaryContainerAlt} style={{ fontWeight: 300 }}>
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
          <span style={{
            fontSize: '14px',
            fontWeight: '300',
            color: theme.palette.oneqrew.primaryContainerAlt,
            letterSpacing: '0.3px'
          }}>
            Standort-Modus
          </span>
        </div>
      )}

      {/* Keyword Volume Overlay - OneQrew Design */}
      {console.log('🔥 VOLUME DEBUG:', { keywordVolume, searchKeyword, hasVolume: !!keywordVolume, hasKeyword: !!searchKeyword })}
      {keywordVolume && searchKeyword && (
        <div
          style={{
            position: 'absolute',
            top: '20px',
            right: '10px',
            zIndex: 1000,
            backgroundColor: theme.palette.oneqrew.secondarySurface,
            backdropFilter: 'blur(12px)',
            borderRadius: '8px',
            padding: '0',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
            border: `1px solid ${theme.palette.oneqrew.primaryContainer}20`,
            minWidth: '280px',
            maxWidth: '300px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <div style={{
            backgroundColor: theme.palette.oneqrew.primaryContainer,
            padding: '16px 20px 12px',
            borderBottom: `1px solid ${theme.palette.oneqrew.primaryContainer}`
          }}>
            <div style={{
              fontSize: '16px',
              fontWeight: '300',
              color: theme.palette.oneqrew.primaryContainerAlt,
              marginBottom: '4px'
            }}>
              Suchvolumen Analyse
            </div>
            <div style={{
              fontSize: '14px',
              color: theme.palette.oneqrew.secondarySurface,
              fontWeight: '300'
            }}>
              "{searchKeyword}"
            </div>
          </div>
          
          {/* Content */}
          <div style={{
            padding: '16px 20px 20px'
          }}>
            {/* National Volume */}
            <div style={{
              marginBottom: '16px'
            }}>
              <div style={{
                fontSize: '12px',
                color: theme.palette.oneqrew.primaryContainer,
                fontWeight: '300',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '6px'
              }}>
{keywordVolume.location || 'Deutschland'}
              </div>
              <div style={{
                fontSize: '24px',
                fontWeight: '300',
                color: theme.palette.oneqrew.primaryContainer,
                lineHeight: '1.2'
              }}>
                {keywordVolume.estimatedVolume}
                <span style={{
                  fontSize: '14px',
                  fontWeight: '300',
                  color: theme.palette.text.secondary,
                  marginLeft: '4px'
                }}>
                  / Monat
                </span>
              </div>
            </div>

            {/* Local Volume */}
            {keywordVolume.localVolume && (
              <div style={{
                backgroundColor: `${theme.palette.oneqrew.primaryContainerAlt}20`,
                border: `1px solid ${theme.palette.oneqrew.primaryContainerAlt}60`,
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '16px'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: theme.palette.oneqrew.primaryContainer,
                  fontWeight: '300',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '6px'
                }}>
                  Lokal (15km Radius)
                </div>
                <div style={{
                  fontSize: '20px',
                  fontWeight: '300',
                  color: theme.palette.oneqrew.primaryContainer,
                  lineHeight: '1.2'
                }}>
                  ~{keywordVolume.localVolume}
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '300',
                    color: theme.palette.text.secondary,
                    marginLeft: '4px'
                  }}>
                    / Monat
                  </span>
                </div>
              </div>
            )}

            {/* Population Info */}
            {keywordVolume.localPopulation && (
              <div style={{
                backgroundColor: theme.palette.oneqrew.secondarySurface,
                borderRadius: '8px',
                padding: '8px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: theme.palette.oneqrew.primaryContainer
                }}></div>
                <div style={{
                  fontSize: '12px',
                  color: theme.palette.text.secondary,
                  fontWeight: '300'
                }}>
                  {keywordVolume.localPopulation} Einwohner im Einzugsgebiet
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Konzentrische Kreise um das Zentrum für bessere Visualisierung - OneQrew Design */}
      <Circle
        center={center}
        radius={5000}
        pathOptions={{ color: theme.palette.oneqrew.purple, fillColor: theme.palette.oneqrew.purple, weight: 1, opacity: 0.3, fillOpacity: 0.05 }}
      />
      <Circle
        center={center}
        radius={10000}
        pathOptions={{ color: theme.palette.oneqrew.purple, fillColor: theme.palette.oneqrew.purple, weight: 1, opacity: 0.2, fillOpacity: 0.03 }}
      />
      <Circle
        center={center}
        radius={15000}
        pathOptions={{ color: theme.palette.oneqrew.purple, fillColor: theme.palette.oneqrew.purple, weight: 1, opacity: 0.1, fillOpacity: 0.02 }}
      />
      
      
      {/* Rang-Marker für die verschiedenen Standorte */}
      {Array.isArray(locationRanks) && locationRanks.map((loc, index) => {
        // Prüfe ob dies der Center-Pin ist (gleiche Koordinaten wie Business)
        const isBusinessLocation = companyLocation && 
          Math.abs(loc.lat - companyLocation.lat) < 0.0001 && 
          Math.abs(loc.lng - companyLocation.lng) < 0.0001;
        
        // Verwende immer Rank-Marker (auch für Business-Location)
        const markerIcon = createRankMarker(loc.rank, loc.color);
          
        return (
          <React.Fragment key={index}>
            <Circle
              center={[loc.lat, loc.lng]}
              radius={getRadiusForRank(loc.rank)}
              pathOptions={{ 
                fillColor: getColorForRank(loc.rank),
                color: getColorForRank(loc.rank),
                fillOpacity: getOpacityForRank(loc.rank),
                weight: 1,
              }}
            />
            <Marker
              position={[loc.lat, loc.lng]}
              icon={markerIcon}
            >
              <Popup
                onOpen={() => setOpenPopup(loc)}
                onClose={() => setOpenPopup(null)}
                className="custom-popup"
              >
                <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', maxWidth: 320, border: `1px solid ${theme.palette.oneqrew.primaryContainer}20` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 300, display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.oneqrew.primaryContainer }}>
                      <LocationOnRoundedIcon sx={{ color: getColorForRank(loc.rank), fontWeight: 300 }} />
                      {isBusinessLocation ? `${companyName} (Ihr Standort)` : (loc.locationName || 'Unbekannter Ort')}
                    </Typography>
                    <IconButton onClick={handleClosePopup} size="small" sx={{ color: theme.palette.oneqrew.primaryContainer, p: 0.5 }}>
                      <ClearRoundedIcon fontSize="small" sx={{ fontWeight: 300 }} />
                    </IconButton>
                  </Box>

                  <Chip
                    label={isBusinessLocation ? `Rang #${loc.rank} am Firmenstandort` : getRankStatus(loc.rank).text}
                    size="small"
                    sx={{
                      bgcolor: getRankStatus(loc.rank).color,
                      color: 'white',
                      fontWeight: 500,
                      mb: 2,
                      mt: 0.5,
                      borderRadius: '8px'
                    }}
                  />

                  <Divider sx={{ my: 1.5, borderColor: `${theme.palette.oneqrew.primaryContainer}20` }} />

                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 300, color: theme.palette.oneqrew.primaryContainer }}>
                    Firmen an diesem Standort:
                  </Typography>

                  <Paper
                    variant="outlined"
                    sx={{
                      maxHeight: 200,
                      overflowY: 'auto',
                      p: 0.5,
                      borderRadius: '8px',
                      borderColor: `${theme.palette.oneqrew.primaryContainer}20`,
                      backgroundColor: theme.palette.oneqrew.secondarySurface
                    }}
                  >
                    <List dense disablePadding>
                      {loc.companies?.map((company, idx) => {
                        const isYourCompany = company.placeId === (companyLocation?.placeId || '');
                        const rankStatus = getRankStatus(company.rank);
                        
                        return (
                          <ListItem
                            key={idx}
                            disablePadding
                            sx={{
                              py: 0.75,
                              px: 1,
                              borderRadius: '8px',
                              mb: 0.5,
                              backgroundColor: isYourCompany ? `${rankStatus.color}15` : 'transparent',
                              border: isYourCompany ? `1px solid ${rankStatus.color}30` : 'none',
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip
                                    label={`#${company.rank}`}
                                    size="small"
                                    sx={{
                                      minWidth: 36,
                                      height: 20,
                                      fontSize: '0.7rem',
                                      bgcolor: rankStatus.color,
                                      color: 'white',
                                      fontWeight: 500,
                                      borderRadius: '8px'
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: isYourCompany ? 300 : 300,
                                      fontSize: '0.85rem',
                                      color: isYourCompany ? rankStatus.color : theme.palette.text.primary,
                                    }}
                                  >
                                    {company.name}
                                    {isYourCompany && ' (Ihr Unternehmen)'}
                                  </Typography>
                                </Box>
                              }
                              secondary={company.address || ''}
                              secondaryTypographyProps={{
                                variant: 'caption',
                                sx: {
                                  fontSize: '0.75rem',
                                  mt: 0.5,
                                  display: 'block',
                                  color: theme.palette.text.secondary,
                                  fontWeight: 300
                                }
                              }}
                            />
                          </ListItem>
                        );
                      }) || (
                        <Typography variant="body2" sx={{ p: 1, color: theme.palette.text.secondary, fontWeight: 300 }}>
                          Keine Firmen gefunden.
                        </Typography>
                      )}
                    </List>
                  </Paper>
                </Paper>
              </Popup>
            </Marker>
          </React.Fragment>
        );
      })}
    </MapContainer>
  );
};

export default LocationMap;